import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  Clock,
  Eye,
  User,
  BookOpen,
  Loader2,
  Share2
} from 'lucide-react';

const CATEGORY_LABELS = {
  investissement: "Stratégies d'investissement",
  fiscalite: 'Fiscalité immobilière',
  temoignages: 'Témoignages',
  actualites: 'Actualités',
  guides: 'Guides & Tutoriels',
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0D47A1] animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <BookOpen className="w-16 h-16 text-slate-300" />
        <h2 className="text-xl font-bold text-slate-700">Article non trouvé</h2>
        <Link to="/blog" className="text-[#0D47A1] hover:underline">Retour au blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="w-4 h-4" />
            Retour au blog
          </Link>
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0D47A1] transition-colors"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4" />
            Partager
          </button>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10 overflow-hidden">
        {/* Header */}
        <header className="mb-8">
          <Badge className="mb-4 bg-[#0D47A1]/10 text-[#0D47A1]">
            {CATEGORY_LABELS[article.category] || article.category}
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {article.author_name}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {article.views} vues
            </span>
          </div>
        </header>

        {/* Cover */}
        {article.cover_image && (
          <div className="mb-10 rounded-2xl overflow-hidden">
            <img src={article.cover_image} alt={article.title} className="w-full h-auto" />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-[#0D47A1] prose-strong:text-slate-800 prose-img:rounded-xl blog-content"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-slate-200">
            {article.tags.map(tag => (
              <Badge key={tag} variant="outline" className="text-slate-500">{tag}</Badge>
            ))}
          </div>
        )}
      </article>

      {/* CTA */}
      <section className="bg-slate-50 border-t py-12">
        <div className="max-w-2xl mx-auto text-center px-4">
          <p className="text-slate-500 mb-4">Envie de contribuer ?</p>
          <Link
            to="/blog/write"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity"
          >
            Rédiger un article
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogArticlePage;
